import type { HTMLAttributes, JSX, UIEventHandler } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { throttle } from 'throttle-debounce';
import { mergeRefs } from '../../helpers/mergeRefs';

const SHADOW_SIZE_PX = 8;

const Container = styled.div`
  overflow: hidden;
  position: relative;
  flex: 1 1 auto;
`;

const ScrollContainer = styled.div<{ $locked?: boolean }>`
  overflow: ${({ $locked }) => ($locked ? 'hidden' : 'auto')};
  height: 100%;
  width: 100%;
`;

const Shadow = styled.div<{ $isVisible: boolean }>`
  pointer-events: none;
  position: absolute;
  transition: box-shadow var(--wui-motion-duration-04) var(--wui-motion-ease);
  box-shadow: ${({ $isVisible }) =>
    $isVisible
      ? `0 0 ${SHADOW_SIZE_PX}px ${SHADOW_SIZE_PX}px var(--wui-color-drop-shadow)`
      : 'none'};
  z-index: 1;
`;

const ShadowAtTop = styled(Shadow)`
  transform: translateY(-${SHADOW_SIZE_PX}px);
  height: 0;
  left: 0;
  right: 0;
  top: 0;
`;

const ShadowAtBottom = styled(Shadow)`
  transform: translateY(${SHADOW_SIZE_PX}px);
  bottom: 0;
  height: 0;
  left: 0;
  right: 0;
`;

const ShadowAtLeft = styled(Shadow)`
  transform: translateX(-${SHADOW_SIZE_PX}px);
  bottom: 0;
  left: 0;
  top: 0;
  width: 0;
`;

const ShadowAtRight = styled(Shadow)`
  transform: translateX(${SHADOW_SIZE_PX}px);
  bottom: 0;
  right: 0;
  top: 0;
  width: 0;
`;

export type ScrollAreaProps = HTMLAttributes<HTMLDivElement> & {
  /**
   * When true, disables scrolling. Useful to temporarily prevent scrolling,
   * like when drag-and-dropping an item from inside to outside of the scroll
   * area.
   */
  locked?: boolean;
};

/**
 * ScrollArea is a simple scrollable container with shadow effects to indicate
 * scrollability.
 */
export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  ({ children, onScroll, style, locked = false, ...props }, forwardedRef): JSX.Element => {
    const scrollContainerRefInternal = useRef<HTMLDivElement>(null);
    const [shadowState, setShadowState] = useState({
      canScrollUp: false,
      canScrollLeft: false,
      canScrollDown: false,
      canScrollRight: false,
    });

    const scrollContainerRef = mergeRefs([scrollContainerRefInternal, forwardedRef]);

    const updateShadows = useCallback(() => {
      const scrollContainer = scrollContainerRefInternal.current;
      if (!scrollContainer) {
        return;
      }

      setShadowState({
        canScrollUp: scrollContainer.scrollTop > 0,
        canScrollLeft: scrollContainer.scrollLeft > 0,
        canScrollDown:
          scrollContainer.scrollTop + scrollContainer.clientHeight < scrollContainer.scrollHeight,
        canScrollRight:
          scrollContainer.scrollLeft + scrollContainer.clientWidth < scrollContainer.scrollWidth,
      });
    }, []);

    const throttledUpdateShadows = useMemo(() => throttle(100, updateShadows), [updateShadows]);

    const onScrollScrollContainer = useCallback<UIEventHandler<HTMLDivElement>>(
      (event) => {
        onScroll?.(event);

        throttledUpdateShadows();
      },
      [onScroll, throttledUpdateShadows],
    );

    useEffect(() => {
      updateShadows();
    }, [updateShadows]);

    return (
      <Container style={style}>
        <ShadowAtTop $isVisible={shadowState.canScrollUp} />
        <ShadowAtBottom $isVisible={shadowState.canScrollDown} />
        <ShadowAtLeft $isVisible={shadowState.canScrollLeft} />
        <ShadowAtRight $isVisible={shadowState.canScrollRight} />
        <ScrollContainer
          ref={scrollContainerRef}
          $locked={locked}
          onScroll={onScrollScrollContainer}
          {...props}
        >
          {children}
        </ScrollContainer>
      </Container>
    );
  },
);

ScrollArea.displayName = 'ScrollArea';
